import { computed, defineComponent, inject, PropType, ref, watch } from 'vue';
import { highlightedMediaBusKey } from '@/common/domain/highlighted-media/HighlightedMediaBus';
import { MediaType } from '@/common/domain/MediaType';
import { FairplayerFallbackImageUi } from '@/common/primary/fairplayer-image/FairplayerFallbackImage.ui';
import { ImageResolution } from '@/common/primary/ImageResolution';

export default defineComponent({
  name: 'FairplayerImage',

  props: {
    highlightable: {
      type: Boolean as PropType<boolean>,
      default: false,
    },
    contain: {
      type: Boolean as PropType<boolean>,
      default: false,
    },
    active: {
      type: Boolean as PropType<boolean>,
      default: false,
    },
    lazy: {
      type: Boolean as PropType<boolean>,
      default: false,
    },
    url: {
      type: String as PropType<string>,
      default: '',
      required: false,
    },
    resolution: {
      type: String as PropType<ImageResolution>,
      required: false,
    },
    fallbackImage: {
      type: String as PropType<FairplayerFallbackImageUi>,
      default: FairplayerFallbackImageUi.EXCLUSIVE,
      required: false,
    },
    alt: {
      type: String as PropType<string>,
      default: 'Image',
      required: false,
    },
    style: {
      type: String as PropType<string>,
      required: false,
    },
  },

  emits: ['load'],

  setup(props, { emit }) {
    const staticFilesUri = import.meta.env.VITE_STATIC_FILES_URL;

    const highlightedMediaBus = inject(highlightedMediaBusKey)!;

    const imageNotSupported = ref(false);
    const imageNotFound = ref(!props.url);

    // eslint-disable-next-line vue/return-in-computed-property
    const fallbackImageName = computed((): string => {
      switch (props.fallbackImage) {
        case FairplayerFallbackImageUi.BANNER:
          return 'banner.jpg';
        case FairplayerFallbackImageUi.EXCLUSIVE:
          return 'exclusive.jpg';
        case FairplayerFallbackImageUi.FAN:
          return 'fan.jpg';
        case FairplayerFallbackImageUi.LOGO:
          return 'logo.png';
      }
    });

    const loading = computed(() => {
      if (props.lazy) {
        return 'lazy';
      }

      return 'eager';
    });

    const filesWithResolution = () => props.resolution && props.url.includes('files.fairplayer');

    const computeSrc = () => `${props.url}${filesWithResolution() ? '?h=' + props.resolution : ''}`;

    const src = ref(computeSrc());

    const onNotFoundImage = () => {
      if (filesWithResolution() && !imageNotSupported.value) {
        imageNotSupported.value = true;
        src.value = props.url;
      } else {
        imageNotFound.value = true;
      }
    };

    const onClick = (url: string) => {
      if (props.highlightable) {
        highlightedMediaBus.open({ activeIndex: 0, medias: [{ url, type: MediaType.IMAGE }] });
      }
    };

    const onLoad = () => {
      emit('load');
    };

    watch(
      () => [props.url],
      () => {
        src.value = computeSrc();
        imageNotFound.value = !props.url;
      }
    );

    return {
      loading,
      fallbackImageName,
      imageNotFound,
      onNotFoundImage,
      onClick,
      staticFilesUri,
      onLoad,
      src,
    };
  },
});
